import React, { useMemo } from 'react'
import { Card, CardContent } from '@duck/ui'
import { PartnerService } from '@duck/api-definitions'
import { useTaskDisplayNames } from '@/modules/entityLookups'
import { NoDataEmptyState } from '@/components/EmptyStates'
import { ClipboardDocumentListIcon, LinkIcon } from '@heroicons/react/24/outline'
import { AssignmentsListSkeleton } from './AssignmentsListSkeleton'
import { useTar } from '@/modules'
import { PublicAssignmentEntry } from './PublicAssignmentEntry'
import { usePersons } from '@/hooks/usePersons'
import { CohortPersonDetails } from '../cohortPageData'

interface PublicAssignmentsListProps {
  publicAssignments: PartnerService.Assignment[]
  assignmentsDetails?: PartnerService.AssignmentDetails[]
  onAssignmentChange: () => void
  onCreatePublicAssignment?: () => void
  skeleton?: boolean
}

export const PublicAssignmentsList: React.FC<PublicAssignmentsListProps> = ({
  publicAssignments,
  assignmentsDetails = [],
  onAssignmentChange,
  onCreatePublicAssignment,
  skeleton = false
}) => {
  const { t } = useTar()

  const publicAssignmentIds = useMemo(
    () => new Set(publicAssignments.map(a => a.id)),
    [publicAssignments]
  )

  const publicAssignmentDetailsAndPersonIds = useMemo(() => {
    if (!assignmentsDetails.length) return { detailsForPublic: [] as PartnerService.AssignmentDetails[], personIds: [] as string[] }
    const detailsForPublic = assignmentsDetails.filter(ad => publicAssignmentIds.has(ad.assignmentId))
    const personIds = Array.from(new Set(detailsForPublic.map(ad => ad.personId)))
    return { detailsForPublic, personIds }
  }, [assignmentsDetails, publicAssignmentIds])

  const { persons } = usePersons(publicAssignmentDetailsAndPersonIds.personIds)

  const peopleDetailsByAssignmentId = useMemo(() => {
    const { detailsForPublic } = publicAssignmentDetailsAndPersonIds
    if (!detailsForPublic.length) return {} as Record<string, CohortPersonDetails[]>
    const personsMap = new Map(persons.map(p => [p.id, p]))
    const result: Record<string, CohortPersonDetails[]> = {}
    detailsForPublic.forEach(assignmentDetail => {
      const { assignmentId, personId } = assignmentDetail
      if (!result[assignmentId]) result[assignmentId] = []
      const person = personsMap.get(personId)
      if (person) {
        result[assignmentId].push({
          personId,
          email: person.email,
          onboardingStatus: person.onboardingStatus,
          assignmentStatus: assignmentDetail.status,
          challengeId: assignmentDetail.challengeId,
          grade: assignmentDetail.grade,
          feedback: assignmentDetail.feedback,
          solution: assignmentDetail.solution,
          startDate: assignmentDetail.startDate,
          submitDate: assignmentDetail.submitDate,
          workTime: assignmentDetail.workTime,
          charts: assignmentDetail.charts,
          queries: assignmentDetail.queries,
        })
      }
    })
    return result
  }, [publicAssignmentDetailsAndPersonIds, persons])

  const taskIds = useMemo(() => publicAssignments.map(a => a.taskId), [publicAssignments])

  const { getDisplayName, isLoading: isTaskLoading } = useTaskDisplayNames(taskIds, {
    fallback: t('common.na')
  })

  if (skeleton) {
    return <AssignmentsListSkeleton variant="detailed" />
  }

  if (publicAssignments.length === 0) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="pt-6">
            <NoDataEmptyState
              icon={onCreatePublicAssignment ? LinkIcon : ClipboardDocumentListIcon}
              title={t('partner.noPublicAssignmentsYet')}
              description={t('partner.noPublicAssignmentsDescription')}
              buttonText={onCreatePublicAssignment ? t('partner.createPublicAssignment') : undefined}
              onButtonClick={onCreatePublicAssignment}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {publicAssignments.map(assignment => {
        const peopleDetails = peopleDetailsByAssignmentId[assignment.id] ?? []
        const assignmentPersons = persons.filter(p => (assignment.personIds ?? []).includes(p.id))
        return (
          <PublicAssignmentEntry
            key={assignment.id}
            assignment={assignment}
            peopleDetails={peopleDetails}
            persons={assignmentPersons}
            getDisplayName={getDisplayName}
            isTaskLoading={isTaskLoading}
            onAssignmentChange={onAssignmentChange}
          />
        )
      })}
    </div>
  )
}
